import * as React from "react";
import { useState, useRef } from "react";
import { Controller, useForm } from "react-hook-form";

import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useResponsive } from "../../hooks/useResponsive";

import { COUNTRY_CODE, DEFAULT_COUNTRY_CODE } from "../../utils/utils";
import { updateProfile } from "../../redux/profile/action";

import { yupResolver } from "@hookform/resolvers/yup";
import storePersist from "../../services/localStorage";
import { localConstant } from "../../services/localStorage";

import {
  Box,
  Button,
  Stack,
  TextField,
  Typography,
  Autocomplete,
  Divider,
  Grid,
  Select,
  MenuItem,
} from "@mui/material";

import NavBar from "../../NavBar";
import MenuBar from "../menuBar";
import ConfirmationDialog from "../../components/ConfirmationDialog";
import CustomTextField from "../../components/CustomTextField";

import { getSubmitData } from "./profileMapper";
import editProfileSchema from "../../validations/editProfileSchema";
import { useTranslation } from "react-i18next";
import "./styles.css";
import { isMobileDeviceCheck } from "../../utils";

const EditProfile = ({
  enableNav = true,
  isDialog = false,
  resetDialog = () => {},
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const { t } = useTranslation();
  const { stateObject } = useSelector((state) => state.driverReducer);

  const profileDataFromRedux = useSelector(
    (state) => state.profileReducer?.profileDetails,
  );

  let profileData = isDialog ? profileDataFromRedux : location.state;

  const smDown = useResponsive("down", "sm");

  const [dialogProps, setDialogProps] = useState({
    isOpen: false,
    title: `${t("pages.profile.key07")} ?`,
    content: `${t("pages.profile.key10")}`,
    reset: () => setDialogProps((prev) => ({ ...prev, isOpen: false })),
    handleContinue: undefined,
  });

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm({
    mode: "all",
    defaultValues: profileData,
    resolver: yupResolver(editProfileSchema),
  });

  const submitForm = (values) => {
    let submitData = {};
    try {
      values.stateCode = stateObject?.USA?.find(
        (item) => item?.description === values.stateCode,
      )?.code;
      values.companyStateCode = stateObject?.USA?.find(
        (item) => item?.description === values.companyStateCode,
      )?.code;
      if (!values?.stateCode) values.stateCode = profileData?.stateCode;
      if (!values?.companyStateCode)
        values.companyStateCode = profileData?.companyStateCode;

      submitData = getSubmitData(values);
    } catch (err) {
      console.log({ err });
    }
    setDialogProps((prev) => ({
      ...prev,
      isOpen: true,
      handleContinue: () => {
        dispatch(
          updateProfile(values.userId, submitData, (response) => {
            if (response?.user && response?.customer) {
              if (smDown) {
                navigate(-1);
              } else {
                resetDialog();
                window.location.reload();
              }
            } else {
              setDialogProps((prev) => ({ ...prev, isOpen: false }));
            }
          }),
        );
      },
    }));
  };

  const myRef = useRef({});
  const userType = storePersist.get(localConstant.USER_PROFILE);

  const renderPersonalItems = (
    <Stack spacing={1}>
      <Controller
        name="firstName"
        control={control}
        render={({ field }) => (
          <CustomTextField
            {...field}
            placeholder={t("commonLabels.text07")}
            label={t("commonLabels.text07")}
            name="firstName"
            required
            type="text"
            error={!!errors.firstName}
            helperText={errors.firstName?.message}
          />
        )}
      />
      <Controller
        name="middleName"
        control={control}
        render={({ field }) => (
          <CustomTextField
            {...field}
            placeholder={t("commonLabels.text15")}
            label={t("commonLabels.text15")}
            name="middleName"
            type="text"
            error={!!errors.middleName}
            helperText={errors.middleName?.message}
          />
        )}
      />
      <Controller
        name="lastName"
        control={control}
        render={({ field }) => (
          <CustomTextField
            {...field}
            placeholder={t("commonLabels.text08")}
            required
            label={t("commonLabels.text08")}
            name="lastName"
            type="text"
            error={!!errors.lastName}
            helperText={errors.lastName?.message}
          />
        )}
      />
      <Controller
        name="userEmail"
        control={control}
        render={({ field }) => (
          <CustomTextField
            label={t("commonLabels.text09")}
            {...field}
            placeholder={t("commonLabels.text09")}
            disabled
            name="userEmail"
            type="email"
            error={!!errors.userEmail}
            helperText={errors.userEmail?.message}
          />
        )}
      />
      <Grid
        display={"flex"}
        flexDirection={"row"}
        justifyContent={"space-between"}
      >
        <Grid mt={"2px"}>
          <Typography fontWeight={"bold"} fontSize={"14px"}>
            Country Code
          </Typography>
          <Controller
            mt={1}
            name="countryCode"
            control={control}
            render={({ field }) => (
              <Select
                renderValue={(value) =>
                  value?.length
                    ? Array.isArray(value)
                      ? value.join(", ")
                      : value
                    : "Country Code"
                }
                sx={{
                  width: isMobileDeviceCheck() ? 80 : 100,
                }}
                labelId="demo-simple-select-autowidth-label"
                id="demo-simple-select-autowidth"
                autoWidth
                label="Country Code"
                {...field}
              >
                {Object.keys(COUNTRY_CODE).map((item) => {
                  return <MenuItem value={item}>{COUNTRY_CODE[item]}</MenuItem>;
                })}
              </Select>
            )}
          ></Controller>
          <p style={{ color: "#d32f2f", fontSize: "12px" }}>
            {errors.countryCode?.message}
          </p>
        </Grid>
        <Grid width={"70%"}>
          <Controller
            name="phoneNumber"
            control={control}
            render={({ field }) => (
              <CustomTextField
                label={t("pages.profile.key01")}
                {...field}
                placeholder={t("pages.profile.key01")}
                required
                name="phoneNumber"
                type="tel"
                error={!!errors.phoneNumber}
                helperText={errors.phoneNumber?.message}
              />
            )}
          />
        </Grid>
      </Grid>

      <Controller
        name="addressLine1"
        control={control}
        render={({ field }) => (
          <CustomTextField
            {...field}
            required
            placeholder={t("commonLabels.text11")}
            label={t("commonLabels.text11")}
            name="addressLine1"
            type="text"
            error={!!errors.addressLine1}
            helperText={errors.addressLine1?.message}
          />
        )}
      />
      <Stack spacing={1} direction={smDown ? "column" : "row"}>
        <Controller
          name="postalCode"
          control={control}
          render={({ field }) => (
            <CustomTextField
              label={t("commonLabels.text14")}
              {...field}
              placeholder={t("commonLabels.text14")}
              required
              name="postalCode"
              type="tel"
              error={!!errors.postalCode}
              helperText={errors.postalCode?.message}
            />
          )}
        />
        <Controller
          name="city"
          control={control}
          render={({ field }) => (
            <CustomTextField
              label={t("commonLabels.text12")}
              {...field}
              required
              placeholder={t("commonLabels.text12")}
              name="city"
              type="text"
              error={!!errors.city}
              helperText={errors.city?.message}
            />
          )}
        />
        <Controller
          name="stateCode"
          control={control}
          render={({ field: { onChange, value } }) => {
            return (
              <Box width={smDown ? "auto" : 0.38}>
                <Typography
                  variant="customLable"
                  color="common.inputCustomLable"
                  marginBottom={1}
                  style={{ fontSize: "14px" }}
                >
                  {t("commonLabels.text13")}
                  <span style={{ color: "#C50F1F" }}>*</span>
                </Typography>

                <Autocomplete
                  name="stateCode"
                  options={
                    stateObject?.USA?.map((item, index) => item?.description) ||
                    []
                  }
                  value={
                    stateObject?.USA?.find((item) =>
                      value?.length === 2
                        ? item?.code === value
                        : item?.description === value,
                    )?.description || ""
                  }
                  getOptionLabel={(option) => option}
                  isOptionEqualToValue={(option, value) => option === value}
                  onChange={(event, item) => {
                    onChange(item);
                  }}
                  renderInput={(params) => (
                    <TextField
                      name="stateCode"
                      {...params}
                      error={!!errors.stateCode}
                      helperText={errors.stateCode?.message}
                      fullWidth
                    />
                  )}
                />
              </Box>
            );
          }}
        />
      </Stack>
    </Stack>
  );

  const CompanyInformationHeader = () => (
    <Stack spacing={1} justifyContent={"center"} alignItems="flex-start">
      <Divider width={"100%"} />
      <Typography variant="titleText" color="primary.main" marginLeft={2}>
        {t("pages.profile.title01")}
      </Typography>
      <Divider width={"100%"} />
    </Stack>
  );

  const renderCompanyItems = (
    <Stack spacing={1} mt={3}>
      <CompanyInformationHeader />
      <Controller
        name="companyName"
        control={control}
        render={({ field }) => (
          <CustomTextField
            {...field}
            disabled={false}
            required
            placeholder={t("pages.companyDetails.text04")}
            label={t("pages.companyDetails.text04")}
            name="companyName"
            type="text"
            error={!!errors.companyName}
            helperText={errors.companyName?.message}
          />
        )}
      />
      <Controller
        name="companyEmail"
        control={control}
        render={({ field }) => (
          <CustomTextField
            label={t("commonLabels.text09")}
            {...field}
            required
            placeholder={t("commonLabels.text09")}
            disabled={userType !== "OWNER"}
            name="companyEmail"
            type="email"
            error={!!errors.companyEmail}
            helperText={errors.companyEmail?.message}
          />
        )}
      />

      {/* here need to write  ---- */}

      <Grid
        display={"flex"}
        flexDirection={"row"}
        justifyContent={"space-between"}
      >
        <Grid mt={"2px"}>
          <Typography fontWeight={"bold"} fontSize={"14px"}>
            Country Code
          </Typography>
          <Controller
            mt={1}
            name="companyCountryCode"
            control={control}
            render={({ field }) => (
              <Select
                renderValue={(value) =>
                  value?.length
                    ? Array.isArray(value)
                      ? value.join(", ")
                      : value
                    : "Company Country Code"
                }
                sx={{
                  width: isMobileDeviceCheck() ? 80 : 100,
                }}
                labelId="demo-simple-select-autowidth-label"
                id="demo-simple-select-autowidth"
                autoWidth
                label="Company Country Code"
                {...field}
              >
                {Object.keys(COUNTRY_CODE).map((item) => {
                  return <MenuItem value={item}>{COUNTRY_CODE[item]}</MenuItem>;
                })}
              </Select>
            )}
          ></Controller>
          <p style={{ color: "#d32f2f", fontSize: "12px" }}>
            {errors.companyCountryCode?.message}
          </p>
        </Grid>
        <Grid width={"70%"}>
          <Controller
            name="companyPhoneNumber"
            control={control}
            render={({ field }) => (
              <CustomTextField
                label={t("pages.profile.key01")}
                {...field}
                placeholder={t("pages.profile.key01")}
                required
                name="companyPhoneNumber"
                type="tel"
                error={!!errors.companyPhoneNumber}
                helperText={errors.companyPhoneNumber?.message}
              />
            )}
          />
        </Grid>
      </Grid>

      <Controller
        name="companyAddressLine1"
        control={control}
        render={({ field }) => (
          <CustomTextField
            {...field}
            required
            placeholder={t("commonLabels.text11")}
            label={t("commonLabels.text11")}
            name="companyAddressLine1"
            type="text"
            error={!!errors.companyAddressLine1}
            helperText={errors.companyAddressLine1?.message}
          />
        )}
      />
      <Stack spacing={1} direction={smDown ? "column" : "row"}>
        <Controller
          name="companyPostalCode"
          control={control}
          render={({ field }) => (
            <CustomTextField
              label={t("commonLabels.text14")}
              {...field}
              placeholder={t("commonLabels.text14")}
              required
              name="companyPostalCode"
              type="tel"
              error={!!errors.companyPostalCode}
              helperText={errors.companyPostalCode?.message}
            />
          )}
        />
        <Controller
          name="companyCity"
          control={control}
          render={({ field }) => (
            <CustomTextField
              label={t("commonLabels.text12")}
              {...field}
              required
              placeholder={t("commonLabels.text12")}
              name="companyCity"
              type="text"
              error={!!errors.companyCity}
              helperText={errors.companyCity?.message}
            />
          )}
        />
        <Controller
          name="companyStateCode"
          control={control}
          render={({ field: { onChange, value } }) => (
            <Box width={smDown ? "auto" : 0.38}>
              <Typography
                variant="customLable"
                color="common.inputCustomLable"
                marginBottom={1}
                style={{ fontSize: "14px" }}
              >
                {t("commonLabels.text13")}
                <span style={{ color: "#C50F1F" }}>*</span>
              </Typography>

              <Autocomplete
                name="companyStateCode"
                options={
                  stateObject?.USA?.map((item, index) => item?.description) ||
                  []
                }
                value={
                  stateObject?.USA?.find((item) =>
                    value?.length === 2
                      ? item?.code === value
                      : item?.description === value,
                  )?.description || ""
                }
                getOptionLabel={(option) => option}
                isOptionEqualToValue={(option, value) => option === value}
                onChange={(event, item) => {
                  onChange(item);
                }}
                renderInput={(params) => (
                  <TextField
                    name="companyStateCode"
                    {...params}
                    error={!!errors.companyStateCode}
                    helperText={errors.companyStateCode?.message}
                    fullWidth
                  />
                )}
              />
            </Box>
          )}
        />
      </Stack>
    </Stack>
  );

  return (
    <div
      style={{
        width: "100%",
        marginLeft: 0,
      }}
    >
      {enableNav && (
        <NavBar
          title={t("pages.editProfile.pageHeading")}
          myRef={myRef}
          enableMenu={false}
        />
      )}
      <MenuBar myRef={myRef} />
      <Box
        sx={{
          width: "100%",
          margin: "auto",
          padding: "0 5%",
        }}
      >
        <form
          onSubmit={handleSubmit((data) => submitForm(data))}
          style={{ marginTop: "3%" }}
        >
          {renderPersonalItems}

          {userType === "OWNER" && renderCompanyItems}
          <Button
            type="submit"
            variant="contained"
            alignSelf={"center"}
            style={{
              alignSelf: "center",
              width: 199,
              height: 32,
              marginLeft: 88,
              alignItems: "center",
              margin: "auto",
              marginTop: isDialog ? 20 : 50,
              marginBottom: isDialog ? 20 : 50,
              display: "flex",
            }}
            className="update_button"
          >
            <Typography variant="buttonText" color="common.buttonText">
              {t("commonLabels.text16")}
            </Typography>
          </Button>
        </form>
      </Box>
      {dialogProps.isOpen && <ConfirmationDialog {...dialogProps} />}
    </div>
  );
};

export default EditProfile;
